// Cache for API responses
const FEE_CACHE = {
  btc: { timestamp: 0, data: null },
  trx: { timestamp: 0, data: null },
};
const CACHE_TTL = 12000000; // 2 minutes

// BTC Fee Estimation (mempool.space)
export const fetchBtcFeeEstimate = async () => {
  try {
    const now = Date.now();
    if (FEE_CACHE.btc.data && now - FEE_CACHE.btc.timestamp < CACHE_TTL) {
      return FEE_CACHE.btc.data;
    }

    const response = await fetch(
      "https://mempool.space/api/v1/fees/recommended"
    );
    const data = await response.json();

    FEE_CACHE.btc = {
      data: {
        fast: data.fastestFee, // ~10 min confirmation
        medium: data.halfHourFee, // ~30 min
        slow: data.hourFee, // ~60 min
      },
      timestamp: now,
    };

    return FEE_CACHE.btc.data;
  } catch (error) {
    console.error("BTC fee estimation failed:", error);
    return { fast: 20, medium: 15, slow: 10 }; // Fallback (sat/vByte)
  }
};


// TRON Energy Cost (TronGrid)
export const fetchTronEnergyCost = async () => {
  try {
    const now = Date.now();
    if (FEE_CACHE.trx.data && now - FEE_CACHE.trx.timestamp < CACHE_TTL) {
      return FEE_CACHE.trx.data;
    }

    const response = await fetch(
      "https://api.trongrid.io/wallet/getchainparameters"
    );
    const { chainParameter } = await response.json();
    const energyFee =
      chainParameter.find((p) => p.key === "getEnergyFee")?.value || 420; // Default: 420 SUN

    FEE_CACHE.trx = {
      data: energyFee / 1e6, // Convert SUN → TRX
      timestamp: now,
    };

    return FEE_CACHE.trx.data;
  } catch (error) {
    console.error("TRON energy cost check failed:", error);
    return 0.00042; // Fallback (~0.42 TRX)
  }
};

// Main Fee Calculation
export const calculateFees = async (
  amount,
  chain,
  exchangeRates = { btc: 1, trx: 1, bnb: 1 },
  options = {}
) => {
  const amountNum = Number(amount) || 0;
  const { btcFeeRate = "medium" } = options;

  // Platform fee: 3%, minimum $3, capped at $25
  const platformFee = Math.min(Math.max(amountNum * 0.03, 3), 25);

  // Network fees
  let networkFeeCrypto = 0;
  let networkDetails = {};

  if (chain === "btc") {
    const btcFeeRates = await fetchBtcFeeEstimate();
    const satPerVbyte = btcFeeRates[btcFeeRate] || btcFeeRates.medium;
    networkFeeCrypto = (satPerVbyte * 140) / 1e8; // BTC
    networkDetails = { btcFeeRates, selectedRate: satPerVbyte };
  } else if (chain === "trx" || chain === "trx-native") {
    const energyCost = await fetchTronEnergyCost();
    networkFeeCrypto = 0.3 + energyCost; // ≈0.3-0.7 TRX
    networkDetails = { energyCost };
  } else if (chain === "bnb") {
    networkFeeCrypto = 0.0001; // ~$0.03-0.10 at typical BNB price
    networkDetails = {};
  }

  const networkFeeUsd = networkFeeCrypto * (exchangeRates[chain] ?? 1);
  const totalFeeUsd = platformFee + networkFeeUsd;

  return {
    platformFee,
    networkFeeCrypto,
    networkFeeUsd,
    totalFeeUsd,
    exchangeRates,
    ...networkDetails,
  };
};
